import React, { useState } from 'react'
import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'
import notFound from "../../../containers/Contacts/Table/NotFoundData"
import { handleTableId } from "../../../helpers/helper"
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal"
import ContactGroupInUseModal from "./ContactGroupInUseModal"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { useDispatch } from "react-redux"
import { removeContactGroup } from "../../../features/contactGroups/contactGroups"
import { useNavigate } from "react-router-dom"
import { LuKey, LuPencil, LuTrash2 } from "react-icons/lu"
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../../components/UiKit/UiKitContext"
import { failureOf } from "../../../utils/errorHelpers"


// Local styled component removed in favor of global theme overrides

/**
 * A grid cell for free text that may not fit its column.
 *
 * MUI's DEFAULT cell is what normally supplies the ellipsis — a custom `renderCell` replaces
 * it, so truncation has to be re-stated. The previous cells used `className="flex w-full"`,
 * and `display:flex` defeats `text-overflow` outright: long values were hard-clipped
 * mid-string ("yuliia_tsoma@challe") with no ellipsis and no way to read the rest
 * (QA 2026-07-21, WS 1226395). Block + `truncate` restores the ellipsis, and `title` keeps
 * the full value recoverable on hover.
 */
export const TruncatedCell = ({ value, fallback = '-', ...rest }) => {
  const text = value === 0 ? '0' : value
  return (
    <div className="w-full truncate" title={text || undefined} {...rest}>
      {text || fallback}
    </div>
  )
}

const ContactGroupsTable = ({ data, setIsUpdated, isUpdated }) => {
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState("")
  const [inUseMessage, setInUseMessage] = useState("")
  const { triggerLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const dispatch = useDispatch()

  const deleteContactGroupOrThrow = async (groupId) => {
    const response = await dispatch(removeContactGroup(groupId))
    if (!response?.payload?.data?._id) {
      throw failureOf(response, 'Something went wrong, please try again later')
    }
    setIsUpdated(!isUpdated)
    showSnackBar("success", "Contact Group successfully deleted")
  }

  const deleteContactGroup = async (groupId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(removeContactGroup(groupId))
      if (response?.payload?.data?._id) {
        setIsUpdated(!isUpdated)
        showSnackBar("success", "Contact Group successfully deleted")
      } else if (response?.payload?.code === 'officeInUse') {
        setInUseMessage(response.payload.message)
      } else {
        showSnackBar("error", "Something went wrong, please try again later")
      }
    } catch (error) {
      showSnackBar("error", error.message || 'Something went wrong, please try again later')
    }
    triggerLoading(false)
  }

  const columns = [
    {
      "field": "_id",
      "headerName": "Group Id",
      renderCell: (params) => {
        return (
          <div className="text-primary-main font-semibold">
            {handleTableId(params.row?._id)}
          </div>
        )
      },
      sortable: false,
      flex: 1,
      // minWidth floors the flex column so the uppercase "GROUP ID" header isn't clamped to MUI's 50px default
      minWidth: 110,
      maxWidth: 150
    },
    {
      "field": "name",
      "headerName": "Name",
      renderCell: (params) => {
        return (
          <TruncatedCell key={params?.row?.id} value={params?.row?.name} fallback="" />
        )
      },
      sortable: false,
      flex: 1,
      // flex keeper: minWidth clears the uppercase "NAME" header and still absorbs slack on wide screens
      minWidth: 150
    },
    {
      "field": "description",
      "headerName": "Description",
      sortable: false,
      flex: 1,
      // flex keeper: minWidth clears the uppercase "DESCRIPTION" header (11 chars) and holds width for long free text
      minWidth: 160
    },
    {
      "field": "owners",
      "headerName": "Owners",
      renderCell: (params) => {
        return (
          <div>
            {params?.row?.owners?.map((owner, key) => {
              const label = owner ? `${owner?.firstName} ${owner?.lastName}` : '-'
              return <TruncatedCell key={key} value={label} />
            })}
          </div>
        )
      },
      sortable: false,
      flex: 1,
      // minWidth clears the uppercase "OWNERS" header so the flex column can't be clamped to 50px
      minWidth: 150
    },
    {
      "field": "supervisors",
      "headerName": "Overseer",
      renderCell: (params) => {
        return (
          <div>
            {params?.row?.supervisors?.map((supervisor, key) => {
              const label = supervisor ? `${supervisor?.firstName} ${supervisor?.lastName}` : '-'
              return <TruncatedCell key={key} value={label} />
            })}
          </div>
        )
      },
      sortable: false,
      flex: 1,
      // minWidth clears the uppercase "OVERSEER" header so the flex column can't be clamped to 50px
      minWidth: 150
    },
    {
      "field": "notificationEmail",
      "headerName": "Notification Email",
      renderCell: (params) => {
        // A custom renderCell replaces MUI's default cell, which is what normally supplies the
        // ellipsis — and `flex` here defeated text-overflow outright, so long addresses were
        // hard-clipped mid-string ("yuliia_tsoma@challe") with no way to read the rest.
        // Block + truncate restores the ellipsis; title exposes the full address on hover.
        return <TruncatedCell value={params?.row?.notificationEmail} />
      },
      sortable: false,
      flex: 1,
      // flex keeper: minWidth fits the widest uppercase header "NOTIFICATION EMAIL" (~180px) plus email content
      minWidth: 200
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (params) => {
        const groupId = params?.row?._id
        const removeFunc = () => deleteContactGroup(groupId)

        return (
          <div className="flex flex-row items-center space-x-2">
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/contact-groups/edit/${groupId}`)}
              title="Edit Contact Group"
            >
              <LuPencil size={18} className="text-text-secondary" />
            </div>
            <div className="cursor-pointer"
              onClick={() => navigate(`/contact-groups/${groupId}/api-key`)}
              title="Manage API Key"
            >
              <LuKey size={18} className="text-text-secondary" />
            </div>
            <div
              className="cursor-pointer"
              onClick={() => setOpenDeleteTaskModal(groupId)}
              title="Delete Contact Group"
            >
              <LuTrash2 size={18} className="text-text-secondary" />
            </div>


            {openDeleteTaskModal === groupId && (kitConfirm ? (
              <ConfirmDialog
                open={Boolean(openDeleteTaskModal)}
                onClose={() => setOpenDeleteTaskModal("")}
                title={params?.row?.name ? `Delete the contact group "${params.row.name}"?` : "Delete this contact group?"}
                impact={["The contact group is removed from Settings.", "This cannot be undone."]}
                confirmLabel="Delete contact group"
                tone="danger"
                onConfirm={() => deleteContactGroupOrThrow(groupId)}
              />
            ) : (
              <ConfirmModal
                leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
                title={`Delete the contact group "${params?.row?.name ?? ''}"?`}
                modalButtonTitle={"Delete"}
                classes=""
                open={openDeleteTaskModal}
                setOpen={setOpenDeleteTaskModal}
                functionButton={removeFunc}
              >
                It can't be restored.
              </ConfirmModal>
            ))}
          </div>
        )
      },
      // icon-only column: fixed width (no flex) sized to clear the uppercase "ACTION" header and the three action icons
      width: 120,
      sortable: false
    }
  ]

  return (
    <div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={75}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
        }}
        columnVisibilityModel={{
          id: false
        }}
        rows={data}
        apiRef={apiRef}
        getRowId={(row) => row._id}
        hideFooterPagination={true}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) => params?.row?.status === 'Failed' ? 'bg-error-light' : ''}
      />
      {inUseMessage && (
        <ContactGroupInUseModal
          message={inUseMessage}
          onClose={() => setInUseMessage("")}
        />
      )}
    </div>
  )
}

export default ContactGroupsTable
